/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {
  logIdentifyUserError,
  logIdentifyUserInitializationError,
  logIdentifyUserSuccess,
  logInitializeAvailableError,
  logInitializeDependencyError,
  logInitializeSuccess,
  logReportCustomErrorFailure,
  logReportCustomErrorSuccess,
  logReportErrorFailure,
  logReportErrorSuccess
} from './DynatraceLogger';

declare const globalThis: any;

/**
 * Provides the type definition for the optional user properties that can be sent
 * to Dynatrace as session-level properties alongside the user identity. These
 * properties appear as filterable attributes in the Dynatrace RUM dashboard.
 */
export interface DynatraceUserProperties {
  username?: string;
  accountName?: string;
}

/**
 * Provides the type definition for the Dynatrace RUM `dtrum` (Dynatrace Real-Time
 * User Monitoring) global API which is exposed via the Dynatrace OneAgent JavaScript
 * script injection once it has been loaded and initialized.
 */
interface DtrumApi {
  reportError(error: Error): void;
  reportCustomError(
    name: string,
    message: string,
    stacktrace?: string,
    parentActionId?: number
  ): void;
  enterAction(name: string, type?: string, startTime?: number): number;
  leaveAction(actionId: number, stopTime?: number, startTime?: number): void;
  identifyUser(userId: string): void;
  sendSessionProperties(
    javaLong?: Record<string, number> | null,
    date?: Record<string, Date> | null,
    shortString?: Record<string, string>,
    longString?: Record<string, string>
  ): void;
}

/**
 * The Dynatrace API provides the single point of entry for all Dynatrace RUM
 * (Real User Monitoring) integrations and configuration.
 *
 * The process of integrating Dynatrace within iOn Applications is as follows:
 *
 * 1.) Invoke `Dynatrace.initialize()` to verify the `dtrum` API is available
 * 2.) Upon successful user authentication, invoke `Dynatrace.identifyUser(userId)`
 *     with optional `DynatraceUserProperties` to send the user's username and
 *     account name as filterable session properties in the Dynatrace RUM
 *     dashboard
 *
 * ```typescript
 *  import Dynatrace from '@ion/core/src/analytics/Dynatrace';
 *
 *  // verify the OneAgent has loaded and dtrum is available ...
 *  Dynatrace.initialize();
 *
 *  // upon successful user authentication, tag the RUM session ...
 *  Dynatrace.identifyUser(user.id, {
 *    username    : user.username,
 *    accountName : user.account?.title
 *  });
 * ```
 */
class Dynatrace {
  /**
   * @private
   */
  static _initialized = false;

  /**
   * Indicates whether Dynatrace RUM has been successfully initialized and
   * the `dtrum` API is available for use. This method should be used to
   * guard calls to other Dynatrace methods when invocation should be
   * conditional.
   *
   * ```typescript
   * import Dynatrace from '@ion/core/src/analytics/Dynatrace';
   *
   *  if (Dynatrace.isEnabled()) {
   *    Dynatrace.identifyUser(user.id);
   *  }
   * ```
   *
   * @returns `true` if `Dynatrace.initialize()` has been called and the
   *          `dtrum` API is available, otherwise `false`.
   */
  static isEnabled(): boolean {
    return Dynatrace._initialized && !!getDtrum();
  }

  /**
   * Verifies that the Dynatrace OneAgent RUM script has loaded and the global
   * `dtrum` API is available. Unlike Pendo (which requires an explicit injector
   * call), Dynatrace self-initializes when the CDN script loads — this method
   * simply confirms readiness.
   *
   * ```typescript
   *
   *   import Dynatrace from '@ion/core/src/analytics/Dynatrace';
   *
   *   Dynatrace.initialize();
   *
   * ```
   */
  static initialize(): boolean {
    if (!Dynatrace._initialized) {
      if (getDtrum()) {
        logInitializeSuccess();
        return (Dynatrace._initialized = true);
      } else {
        logInitializeDependencyError();
      }
    } else {
      logInitializeAvailableError();
    }
    return false;
  }

  /**
   * Tags the current RUM session with the authenticated user's identity and
   * optionally sends additional user properties (username, account name) as
   * filterable session-level attributes in the Dynatrace RUM dashboard. Must
   * be called after successful user authentication, mirroring the pattern
   * used by `Pendo.initialize(user)`.
   *
   * ```typescript
   *   import Dynatrace from '@ion/core/src/analytics/Dynatrace';
   *
   *   // upon successful user authentication ...
   *   Dynatrace.identifyUser(user.id, {
   *     username    : user.username,
   *     accountName : user.account?.title
   *   });
   * ```
   *
   * @param userId     The unique identifier of the authenticated user.
   * @param properties Optional user properties to send as session attributes.
   */
  static identifyUser(userId: string, properties?: DynatraceUserProperties): boolean {
    if (Dynatrace._initialized) {
      const dtrum = getDtrum();
      if (dtrum) {
        dtrum.identifyUser(userId);
        if (properties) {
          const sessionProperties = createSessionProperties(properties);
          dtrum.sendSessionProperties(null, null, sessionProperties);
        }
        logIdentifyUserSuccess();
        return true;
      } else {
        logIdentifyUserError();
      }
    } else {
      logIdentifyUserInitializationError();
    }
    return false;
  }

  /**
   * Reports a JavaScript error to Dynatrace RUM. The error will appear in the
   * Dynatrace session explorer under JavaScript errors.
   *
   * ```typescript
   *  import Dynatrace from '@ion/core/src/analytics/Dynatrace';
   *
   *  try {
   *     ...
   *  } catch (err) {
   *    Dynatrace.reportError(err);
   *  }
   * ```
   *
   * @param error The Error instance to report to Dynatrace.
   */
  static reportError(error: Error): boolean {
    if (Dynatrace._initialized) {
      const dtrum = getDtrum();
      if (dtrum) {
        dtrum.reportError(error);
        logReportErrorSuccess();
        return true;
      }
    }
    logReportErrorFailure();
    return false;
  }

  /**
   * Reports a custom error with a name, message, and optional stacktrace to
   * Dynatrace RUM. This is the primary method used by the `DynatraceBeacon`
   * for sending crash and page performance events.
   *
   * ```typescript
   *  import Dynatrace from '@ion/core/src/analytics/Dynatrace';
   *
   *  Dynatrace.reportCustomError('PageCrash', 'Unhandled error', error.stack);
   *
   * ```
   *
   * @param name       The name categorizing the custom error that is to be tracked.
   * @param message    A descriptive message providing information about the error.
   * @param stacktrace An optional stacktrace containing the root cause error string.
   */
  static reportCustomError(name: string, message: string, stacktrace?: string): boolean {
    if (Dynatrace._initialized) {
      const dtrum = getDtrum();
      if (dtrum) {
        dtrum.reportCustomError(name, message, stacktrace);
        logReportCustomErrorSuccess();
        return true;
      }
    }
    logReportCustomErrorFailure();
    return false;
  }

  /**
   * Starts a custom user action in Dynatrace. Returns an `actionId` that must
   * be passed to `leaveAction()` to complete the action measurement.
   *
   * ```typescript
   *  import Dynatrace from '@ion/core/src/analytics/Dynatrace';
   *
   *  const actionId = Dynatrace.enterAction('Load Dashboard');
   *
   *  // perform work ...
   *  Dynatrace.leaveAction(actionId);
   * ```
   *
   * @param name The display name for the custom tracked user action.
   */
  static enterAction(name: string): number | undefined {
    if (Dynatrace._initialized) {
      const dtrum = getDtrum();
      if (dtrum) {
        return dtrum.enterAction(name);
      }
    }
  }

  /**
   * Completes a custom user action previously started with `enterAction()`.
   *
   * @param actionId The action identifier returned by `enterAction()`.
   */
  static leaveAction(actionId: number): void {
    if (Dynatrace._initialized) {
      getDtrum()?.leaveAction(actionId);
    }
  }

  /**
   * Sends custom session-level properties to Dynatrace for filtering and
   * analysis in the RUM dashboard.
   *
   * ```typescript
   *  import Dynatrace from '@ion/core/src/analytics/Dynatrace';
   *
   *  Dynatrace.sendSessionProperties(
   *    {application: 'ion-suite'},
   *    {environment: 'production'}
   *  );
   * ```
   *
   * @param shortString Short string properties (max 100 chars each).
   * @param longString  Long string properties (max 1000 chars each).
   */
  static sendSessionProperties(
    shortString?: Record<string, string>,
    longString?: Record<string, string>
  ): boolean {
    if (Dynatrace._initialized) {
      const dtrum = getDtrum();
      if (dtrum) {
        dtrum.sendSessionProperties(null, null, shortString, longString);
        return true;
      }
    }
    return false;
  }
};

/**
 * @private
 *
 * Creates a Dynatrace session properties object from the provided user
 * properties. Only non-undefined values are included in the resulting
 * session properties object.
 *
 * @param   properties The user properties to convert.
 * @returns A record of string key-value pairs for session properties.
 */
const createSessionProperties = (
  properties: DynatraceUserProperties): Record<string, string> => {
  const result: Record<string, string> = {};
  const {username, accountName} = properties || {};
  if (username) {
    result.username = username;
  }
  if (accountName) {
    result.accountName = accountName;
  }
  return result;
};

/**
 * @private
 *
 * Provides a convenience accessor for the Dynatrace `dtrum` global API.
 *
 * @returns The `dtrum` API if available, otherwise `undefined`.
 */
const getDtrum = (): DtrumApi | undefined => globalThis?.dtrum;

/**
 * @private
 *
 * Provides a means of removing the Dynatrace global and resetting the
 * initialized state. This method should only be used for testing purposes.
 */
export const __reset = () => {
  delete globalThis.dtrum;
  Dynatrace._initialized = false;
};

export default Dynatrace;
